import { ChangeDetectorRef, Component, Input, OnDestroy, Optional, TemplateRef, ViewChild } from '@angular/core';
import { CommonAPI } from '@app/core/api/common.service';
import { UtilitiesService } from '@app/core/services/utilities.service';
import { getValueByPath } from '@app/core/utilities/object.utilities';
import { TranslateService } from '@ngx-translate/core';
import { NzNotificationComponent, NzNotificationService } from 'ng-zorro-antd/notification';
import { Subscription } from 'rxjs';
import { take } from 'rxjs/operators';
import { TableLayoutColumn } from '../../modules/table-layout/models/table-layout.model';
import { TableLayoutService } from '../../modules/table-layout/services/table-layout.service';
import { lastValue } from '../../operators/last-value.operator';

export interface ExportConfig {
  fields: string[];
  columns?: string[];
  limit: number;
  url: string;
  refiners: [string, string][];
}

@Component({
  standalone: false,
  selector: 'adspt-export-table-data',
  templateUrl: './export-table-data.component.html',
  styleUrls: ['./export-table-data.component.scss'],
})
export class ExportTableDataComponent implements OnDestroy {
  @ViewChild('template', { static: false }) template: TemplateRef<{ $implicit: NzNotificationComponent; data: any }>;
  @ViewChild('limitTemplate', { static: false }) limitTmpl: TemplateRef<{
    $implicit: NzNotificationComponent;
    data: any;
  }>;
  @Input() config = {} as ExportConfig;
  @Input() icon? = 'download';
  @Input() heapData = {};
  @Input() useConfig?: boolean;

  columnsTranslations = {};
  export = [{ key: 'csv' }, { key: 'xls' }] as any;
  subs$: Subscription[] = [];
  errorToast = false;
  onExport(item: any) {
    if (this.config.limit > 100000) {
      return this.notificationService.template(this.limitTmpl, {
        nzDuration: 5000,
        nzPauseOnHover: true,
        nzAnimate: true,
        nzClass: 'adspt-toast error left',
      });
    }
    const temp = this.notificationService.template(this.template, {
      nzDuration: 7500,
      nzPauseOnHover: true,
      nzAnimate: false,
      nzClass: 'adspt-toast info left',
    });
    temp.messageId = (+new Date()).toString();

    const layout = this.tl && lastValue(this.tl.layout$);

    if (!this.config.columns) {
      this.config.columns = this.config.fields.map((field) => getValueByPath(this.columnsTranslations, field) || '');
    }

    const config = !this.useConfig && layout?.columns.length ? this.getConfigLayout(layout.columns) : this.config;
    const refiners = this.utilitiesService.refinersToUrl(config.refiners);
    this.subs$.push(
      this.commonAPI
        .export(
          `${config.url}?${refiners ? refiners + '&' : ''}limit=${config.limit || 100000}`,
          config.fields.map((d) => d.replace('^', '.')),
          config.columns,
          item.key,
        )
        .pipe(take(1))
        .subscribe(
          () => {
            setTimeout(() => {
              this.notificationService.remove(temp.messageId);
              this.cd.detectChanges();
            }, 1000);
          },
          () => {
            this.notificationService.remove(temp.messageId);
            this.errorToast = true;
            this.cd.detectChanges();
          },
        ),
    );
  }

  constructor(
    private translate: TranslateService,
    private notificationService: NzNotificationService,
    private commonAPI: CommonAPI,
    private utilitiesService: UtilitiesService,
    private cd: ChangeDetectorRef,
    @Optional() private tl?: TableLayoutService,
  ) {
    this.subs$.push(
      this.translate.get(['common.chart-exports', 'export-column-names']).subscribe((response: any) => {
        this.export.forEach((item) => (item.name = response['common.chart-exports'][item.key]));
        this.columnsTranslations = response['export-column-names'];
      }),
    );
  }
  ngOnDestroy() {
    this.subs$.forEach((s) => s.unsubscribe());
  }

  getExtraHeapData(data: any): any {
    return {
      ...(this.heapData || {}),
      ...data,
    };
  }

  private getConfigLayout(columns: TableLayoutColumn[]): any {
    const baseColsIdx = this.config.fields.reduce((indices, field, idx) => {
      if (columns.every(({ id }) => field !== id && field !== `${id}_adjusted`)) {
        indices.push(idx);
      }

      return indices;
    }, []);
    const visibleColumns = columns.filter((col) => col.visible);
    const visibleOrderedFields = visibleColumns.map((col) =>
      this.config.fields.includes(`${col.id}_adjusted`) ? `${col.id}_adjusted` : col.id,
    );
    const visibleOrderedColumns = visibleOrderedFields.map(
      (field, fieldIdx) =>
        this.config.columns[this.config.fields.indexOf(field)] ||
        this.translate.instant(visibleColumns[fieldIdx].title),
    );

    return {
      ...this.config,
      fields: [...baseColsIdx.map((idx) => this.config.fields[idx]), ...visibleOrderedFields],
      columns: [...baseColsIdx.map((idx) => this.config.columns[idx]), ...visibleOrderedColumns],
    };
  }
}
